/* ==========================================================================
   Timeline Component - Asian American Forward Website
   ========================================================================== */

let timelineContainer = null;
let timelineTrack = null;
let leftArrow = null;
let rightArrow = null;
let activePoint = null;
let currentScrollPosition = 0;

const SCROLL_AMOUNT = 300; // pixels to scroll per arrow click

export function initializeTimeline() {
  timelineContainer = document.getElementById('timeline-scroll-container');
  timelineTrack = document.getElementById('timeline-track');
  leftArrow = document.getElementById('timeline-left-arrow');
  rightArrow = document.getElementById('timeline-right-arrow');
  
  if (!timelineContainer || !timelineTrack || !leftArrow || !rightArrow) {
    return;
  }
  
  // Set up timeline points
  setupTimelinePoints();
  
  // Set up arrow functionality
  setupArrowControls();
  
  // Set up outside click handling
  setupOutsideClick();
  
  // Initialize arrow states
  updateArrowStates();
  
  // Start at leftmost position
  scrollToPosition(0);
}

/* ==========================================================================
   Timeline Points Setup
   ========================================================================== */

function setupTimelinePoints() {
  const timelinePoints = timelineTrack.querySelectorAll('.timeline-point');
  
  timelinePoints.forEach((point, index) => {
    // Add click handler
    point.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleTimelinePoint(point);
    });
    
    // Add keyboard support
    point.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        toggleTimelinePoint(point);
      }
    });
  });
}

/* ==========================================================================
   Timeline Point Toggle
   ========================================================================== */

function toggleTimelinePoint(point) {
  const isActive = point.classList.contains('active');
  
  // Close any currently active point
  if (activePoint && activePoint !== point) {
    closeTimelinePoint(activePoint);
  }
  
  if (isActive) {
    closeTimelinePoint(point);
  } else {
    openTimelinePoint(point);
  }
}

function openTimelinePoint(point) {
  // Close any other active point first
  if (activePoint && activePoint !== point) {
    closeTimelinePoint(activePoint);
  }
  
  point.classList.add('active');
  activePoint = point;
  
  // Show the image
  const imageContainer = point.querySelector('.timeline-image-container');
  if (imageContainer) {
    imageContainer.style.display = 'block';
    // Force a reflow to ensure the display change takes effect
    imageContainer.offsetHeight;
    imageContainer.classList.add('visible');
  }
  
  // Update ARIA attributes
  point.setAttribute('aria-expanded', 'true');
}

function closeTimelinePoint(point) {
  point.classList.remove('active');
  
  // Hide the image
  const imageContainer = point.querySelector('.timeline-image-container');
  if (imageContainer) {
    imageContainer.classList.remove('visible');
    // Wait for animation to complete before hiding
    setTimeout(() => {
      if (!point.classList.contains('active')) {
        imageContainer.style.display = 'none';
      }
    }, 300);
  }
  
  // Update ARIA attributes
  point.setAttribute('aria-expanded', 'false');
  
  if (activePoint === point) {
    activePoint = null;
  }
}

/* ==========================================================================
   Arrow Controls
   ========================================================================== */

function setupArrowControls() {
  leftArrow.addEventListener('click', () => scrollLeft());
  rightArrow.addEventListener('click', () => scrollRight());
  
  // Add keyboard support for arrows
  leftArrow.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollLeft();
    }
  });
  
  rightArrow.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      scrollRight();
    }
  });
}

function scrollLeft() {
  const newPosition = Math.max(0, currentScrollPosition - SCROLL_AMOUNT);
  scrollToPosition(newPosition);
}

function scrollRight() {
  const maxScroll = getMaxScrollPosition();
  const newPosition = Math.min(maxScroll, currentScrollPosition + SCROLL_AMOUNT);
  scrollToPosition(newPosition);
}

function scrollToPosition(position) {
  currentScrollPosition = position;
  timelineTrack.style.transform = `translateX(-${position}px)`;
  updateArrowStates();
}

function getMaxScrollPosition() {
  const containerWidth = timelineContainer.offsetWidth;
  const trackWidth = timelineTrack.scrollWidth;
  return Math.max(0, trackWidth - containerWidth);
}

function updateArrowStates() {
  const maxScroll = getMaxScrollPosition();
  
  // Update left arrow
  if (currentScrollPosition <= 0) {
    leftArrow.disabled = true;
    leftArrow.setAttribute('aria-hidden', 'true');
    leftArrow.style.opacity = '0';
    leftArrow.style.pointerEvents = 'none';
  } else {
    leftArrow.disabled = false;
    leftArrow.setAttribute('aria-hidden', 'false');
    leftArrow.style.opacity = '1';
    leftArrow.style.pointerEvents = 'auto';
  }
  
  // Update right arrow
  if (currentScrollPosition >= maxScroll) {
    rightArrow.disabled = true;
    rightArrow.setAttribute('aria-hidden', 'true');
    rightArrow.style.opacity = '0';
    rightArrow.style.pointerEvents = 'none';
  } else {
    rightArrow.disabled = false;
    rightArrow.setAttribute('aria-hidden', 'false');
    rightArrow.style.opacity = '1';
    rightArrow.style.pointerEvents = 'auto';
  }
}

/* ==========================================================================
   Outside Click Handling
   ========================================================================== */

function setupOutsideClick() {
  document.addEventListener('click', (e) => {
    // Check if click is outside timeline or on timeline background
    if (!e.target.closest('.timeline-point') && activePoint) {
      closeTimelinePoint(activePoint);
    }
  });
  
  // Also handle escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activePoint) {
      closeTimelinePoint(activePoint);
    }
  });
}

/* ==========================================================================
   Window Resize Handling
   ========================================================================== */

function handleResize() {
  // Update arrow states when window is resized
  updateArrowStates();
  
  // Ensure we don't scroll beyond limits
  const maxScroll = getMaxScrollPosition();
  if (currentScrollPosition > maxScroll) {
    scrollToPosition(maxScroll);
  }
}

// Add resize listener
window.addEventListener('resize', handleResize);

/* ==========================================================================
   Public API
   ========================================================================== */

export function getTimelineContainer() {
  return timelineContainer;
}

export function closeActiveTimelinePoint() {
  if (activePoint) {
    closeTimelinePoint(activePoint);
  }
}

/* ==========================================================================
   Enhanced Debug Functions
   ========================================================================== */

function addDebugOverlay() {
  console.log('🔧 Adding debug overlay...');
  
  // Remove existing debug overlay
  const existingOverlay = document.getElementById('timeline-debug-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  // Create debug overlay
  const overlay = document.createElement('div');
  overlay.id = 'timeline-debug-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 15px;
    border-radius: 8px;
    font-family: monospace;
    font-size: 12px;
    line-height: 1.4;
    z-index: 10000;
    max-width: 400px;
    word-wrap: break-word;
  `;
  
  document.body.appendChild(overlay);
  
  // Update debug info
  updateDebugInfo();
  
  // Update on resize
  window.addEventListener('resize', updateDebugInfo);
}

function updateDebugInfo() {
  const overlay = document.getElementById('timeline-debug-overlay');
  if (!overlay) return;
  
  const trackRect = timelineTrack.getBoundingClientRect();
  const lineElement = document.querySelector('.timeline-line');
  const lineRect = lineElement ? lineElement.getBoundingClientRect() : null;
  
  // Get all dots
  const dots = timelineTrack.querySelectorAll('.timeline-dot');
  const dotPositions = Array.from(dots).map((dot, index) => {
    const rect = dot.getBoundingClientRect();
    return {
      index,
      centerY: rect.top + rect.height / 2,
      centerX: rect.left + rect.width / 2,
      rect: {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      }
    };
  });
  
  const lineY = lineRect ? Math.round(lineRect.top + lineRect.height / 2) : 'N/A';
  
  overlay.innerHTML = `
    <div style="color: #00ff00; font-weight: bold;">🔧 Timeline Debug Info</div>
    
    <div style="margin: 10px 0; color: #ffff00;">📐 Line Position:</div>
    <div>Y-center: ${lineY}px</div>
    <div>Width: ${lineRect ? Math.round(lineRect.width) : 'N/A'}px</div>
    
    <div style="margin: 10px 0; color: #ff8800;">⚪ Dot Positions:</div>
    ${dotPositions.map(dot => `
      <div style="margin: 5px 0;">
        Dot ${dot.index}: Y-center ${Math.round(dot.centerY)}px
        <div style="margin-left: 10px; font-size: 10px; color: #ccc;">
          Offset from line: ${lineRect ? Math.round(dot.centerY - lineY) : 'N/A'}px
        </div>
      </div>
    `).join('')}
    
    <div style="margin: 10px 0; color: #8888ff;">📏 Track Info:</div>
    <div>Width: ${Math.round(trackRect.width)}px</div>
    <div>Height: ${Math.round(trackRect.height)}px</div>
    <div>Window Width: ${window.innerWidth}px</div>
    
    <div style="margin: 10px 0; font-size: 10px; color: #888;">
      Call toggleTimelineDebug() to remove
    </div>
  `;
}

function removeDebugOverlay() {
  const overlay = document.getElementById('timeline-debug-overlay');
  if (overlay) {
    overlay.remove();
  }
  window.removeEventListener('resize', updateDebugInfo);
}

// Global debug toggle function
window.toggleTimelineDebug = function() {
  const overlay = document.getElementById('timeline-debug-overlay');
  if (overlay) {
    removeDebugOverlay();
    console.log('🔧 Timeline Debug - Overlay removed');
  } else {
    addDebugOverlay();
    console.log('🔧 Timeline Debug - Overlay added with real-time positioning data');
    console.log('📝 Resize the window to see how positions change');
  }
};

// Debug mode available via toggleTimelineDebug() in console
console.log('💡 Timeline Debug: Call toggleTimelineDebug() to show/hide debug overlay');

 